import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { transfersApi, Transfer } from "../api/transfers";
import { vesselsApi } from "../api/vessels";
import { authApi } from "../api/auth";
import { companiesApi } from "../api/companies";

function StatCard({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-2 leading-none">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1.5">{sub}</p>}
    </div>
  );
}

function statusBadge(status: Transfer["status"]) {
  const map: Record<Transfer["status"], string> = {
    delivered:  "bg-green-100 text-green-700",
    pending:    "bg-amber-100 text-amber-700",
    in_transit: "bg-blue-100 text-blue-700",
    cancelled:  "bg-red-100 text-red-700",
  };
  return map[status] ?? "bg-gray-100 text-gray-600";
}

export default function DashboardPage() {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [vesselCount, setVesselCount] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companyCount, setCompanyCount] = useState(0);

  useEffect(() => {
    transfersApi.list().then(setTransfers).catch(console.error);
    vesselsApi.list().then((v) => setVesselCount(v.length)).catch(console.error);
    authApi.me().then((u) => {
      setIsSuperAdmin(u.is_super_admin);
      if (u.is_super_admin) {
        companiesApi.list().then((cs) => setCompanyCount(cs.length)).catch(console.error);
      }
    }).catch(console.error);
  }, []);

  const pending   = transfers.filter((t) => t.status === "pending").length;
  const delivered = transfers.filter((t) => t.status === "delivered").length;

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Page header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {isSuperAdmin ? "Platform overview — all companies" : "Overview of your transfer activity"}
          </p>
        </div>
        <Link
          to="/transfers?new=1"
          className="rounded-lg bg-navy-700 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 transition-colors"
        >
          New Transfer
        </Link>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Transfers" value={transfers.length} />
          <StatCard label="Downloaded"      value={delivered} />
          <StatCard label="Pending"         value={pending} />
          {isSuperAdmin ? (
            <>
              <StatCard label="Fleet Size"  value={vesselCount} sub="across all companies" />
              <StatCard label="Companies"   value={companyCount} sub="registered companies" />
            </>
          ) : (
            <StatCard label="Fleet Size" value={vesselCount} sub="active vessels" />
          )}
        </div>

        {/* Recent transfers */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">Recent Transfers</span>
            <Link to="/transfers" className="text-sm text-navy-700 hover:underline font-medium">
              View all
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vessel</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transfers.slice(0, 8).map((t) => (
                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3 text-gray-800 font-medium truncate max-w-xs">{t.description}</td>
                  <td className="px-5 py-3 text-gray-600">{t.vessel_name ?? "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(t.status)}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500">
                    {new Date(t.created_at).toLocaleDateString([], { day: "numeric", month: "short" })}
                  </td>
                </tr>
              ))}
              {transfers.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-sm text-gray-400">
                    No transfers yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
